/* global React */
(function (global) {
  'use strict';

  // State for the Support route: which sub-page is shown (tickets or articles) and
  // which help article is open on the Articles page. It lives in the app root, next
  // to the other sub-page states, because the address bar mirrors both values
  // (/ovrig/support/artikler?artikkel=<id>). Kept out of app.jsx on purpose.
  const D = 'tickets';

  // Resolved at call time, so a missing appRoute.js degrades to the defaults.
  const routeApi = () => global.PlummeAppRoute || {};

  // Read once, when the app mounts: the URL sync effect rewrites the address after
  // the first render and drops parameters it does not own.
  const initialState = () => {
    const location = global.location || {};
    const url = routeApi().resolvePath ? routeApi().resolvePath(location.pathname) : null;
    const onArticles = Boolean(url && url.route === 'support' && url.subPage === 'articles');
    return {
      page: onArticles ? 'articles' : D,
      articleId: onArticles && routeApi().supportArticleFromSearch
        ? routeApi().supportArticleFromSearch(location.search)
        : '',
    };
  };

  function useSupportRouteState() {
    const [state, setState] = React.useState(initialState);
    // Set when the next address change must replace the history entry instead of
    // adding one (clearing an invalid article id), so Back is not trapped.
    const replaceNext = React.useRef(false);
    // Switching pages always returns to the list, so an open article never leaks
    // into the other page.
    const setPage = React.useCallback((page) => setState({ page: page || D, articleId: '' }), []);
    const setArticleId = React.useCallback((articleId, options) => {
      if (options && options.replace) replaceNext.current = true;
      setState((current) => ({ ...current, articleId: articleId || '' }));
    }, []);
    // Entering Support from another route lands on Tickets, the default page.
    const enterRoute = React.useCallback(() => setState({ page: D, articleId: '' }), []);
    // Browser back/forward: the address is the source of truth.
    const syncFromLocation = React.useCallback((resolved) => {
      const page = (resolved && resolved.subPage) || D;
      const location = global.location || {};
      setState({
        page,
        articleId: page === 'articles' && routeApi().supportArticleFromSearch
          ? routeApi().supportArticleFromSearch(location.search)
          : '',
      });
    }, []);
    // Extra query for the URL sync effect, so the open article stays in the address.
    // Settings callbacks and pending action approvals must survive the second URL
    // sync that adds the business. Approvals can originate on any app route.
    const keepSearch = (route) => {
      const current = global.location ? global.location.search : '';
      try {
        if (new URLSearchParams(current).get('approveAction')) return current;
      } catch (error) { /* Fall through to the route-owned query. */ }
      return route === 'innstillinger'
        ? current
        : route === 'support' && state.page === 'articles' && routeApi().supportArticleSearch
          ? routeApi().supportArticleSearch(state.articleId)
          : '';
    };
    // First sync keeps the incoming query, minus the article param off the Articles page.
    const firstSearch = (route, search) => {
      if (route === 'support' && state.page === 'articles') return search;
      try {
        const params = new URLSearchParams(String(search || ''));
        params.delete(routeApi().ARTICLE_PARAM || 'artikkel');
        return params.toString();
      } catch (error) { return ''; }
    };
    // The first sync and a flagged change replace the history entry; others push.
    const historyMethod = (first) => {
      const replace = first || replaceNext.current;
      replaceNext.current = false;
      return replace ? 'replaceState' : 'pushState';
    };
    return {
      page: state.page,
      articleId: state.articleId,
      setPage,
      setArticleId,
      enterRoute,
      syncFromLocation,
      keepSearch,
      firstSearch,
      historyMethod,
    };
  }

  global.useSupportRouteState = useSupportRouteState;
})(typeof window !== 'undefined' ? window : globalThis);
